import { useState, useEffect } from "react";
import Sidebar from "../components/Sidebar";

const defaultPrices = {
  tmpSmall: "450",
  tmpMedium: "750",
  tmpLarge: "1200",
  tgsSmall: "350",
  tgsMedium: "600",
  tgsLarge: "950",
  tcHourly: "85",
  tcDay: "680",
  tcNight: "850",
};

export default function Prices() {
  const [prices, setPrices] = useState(() => {
    const saved = localStorage.getItem("focusPrices");
    return saved ? JSON.parse(saved) : defaultPrices;
  });
  const [saved, setSaved] = useState(false);
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    if (saved) {
      const timer = setTimeout(() => setSaved(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [saved]);

  const handleChange = (key, value) => {
    setEditing(true);
    setPrices((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = () => {
    localStorage.setItem("focusPrices", JSON.stringify(prices));
    setSaved(true);
    setEditing(false);
  };

  const handleReset = () => {
    setPrices(defaultPrices);
    setEditing(true);
  };

  const PriceRow = ({ label, priceKey }) => (
    <tr className="hover:bg-gray-50 transition-colors">
      <td className="px-6 py-4 text-sm font-medium text-black">{label}</td>
      <td className="px-6 py-4">
        <div className="relative w-36">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">
            $
          </span>
          <input
            type="number"
            value={prices[priceKey]}
            onChange={(e) => handleChange(priceKey, e.target.value)}
            className="w-full pl-7 pr-3 py-2 rounded-lg border border-gray-200 bg-white text-black text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
          />
        </div>
      </td>
    </tr>
  );

  return (
    <div className="flex">
      <Sidebar />
      <main className="flex-1 bg-gray-50 min-h-[calc(100vh-4rem)]">
        <div className="p-6 md:p-8 max-w-4xl">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-black">
                My Prices
              </h1>
              <p className="text-gray-500 mt-1">
                Manage your service rates and pricing.
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={handleReset}
                className="px-4 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-all"
              >
                Reset Defaults
              </button>
              <button
                onClick={handleSave}
                disabled={!editing}
                className={`px-6 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                  editing
                    ? "bg-orange-500 text-white hover:bg-orange-600 hover:shadow-lg hover:shadow-orange-500/25 active:scale-95"
                    : "bg-gray-100 text-gray-400 cursor-not-allowed"
                }`}
              >
                Save Prices
              </button>
            </div>
          </div>

          {/* Success toast */}
          {saved && (
            <div className="bg-green-50 border border-green-200 text-green-600 text-sm rounded-lg px-4 py-3 mb-6 flex items-center gap-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Prices saved successfully!
            </div>
          )}

          {/* TMP Pricing */}
          <div className="bg-white rounded-xl border border-gray-200 mb-6">
            <div className="px-6 py-4 border-b border-gray-100">
              <h2 className="text-lg font-semibold text-black">
                Traffic Management Plans (TMP)
              </h2>
            </div>
            <table className="w-full">
              <thead>
                <tr className="text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  <th className="px-6 py-3">Plan Type</th>
                  <th className="px-6 py-3">Price (AUD)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                <PriceRow label="Small Project TMP" priceKey="tmpSmall" />
                <PriceRow label="Medium Project TMP" priceKey="tmpMedium" />
                <PriceRow label="Large Project TMP" priceKey="tmpLarge" />
              </tbody>
            </table>
          </div>

          {/* TGS Pricing */}
          <div className="bg-white rounded-xl border border-gray-200 mb-6">
            <div className="px-6 py-4 border-b border-gray-100">
              <h2 className="text-lg font-semibold text-black">
                Traffic Guidance Schemes (TGS)
              </h2>
            </div>
            <table className="w-full">
              <thead>
                <tr className="text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  <th className="px-6 py-3">Scheme Type</th>
                  <th className="px-6 py-3">Price (AUD)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                <PriceRow label="Small TGS" priceKey="tgsSmall" />
                <PriceRow label="Medium TGS" priceKey="tgsMedium" />
                <PriceRow label="Large TGS" priceKey="tgsLarge" />
              </tbody>
            </table>
          </div>

          {/* Traffic Control Rates */}
          <div className="bg-white rounded-xl border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-100">
              <h2 className="text-lg font-semibold text-black">
                Traffic Control Rates
              </h2>
            </div>
            <table className="w-full">
              <thead>
                <tr className="text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  <th className="px-6 py-3">Rate Type</th>
                  <th className="px-6 py-3">Price (AUD)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                <PriceRow label="Hourly Rate" priceKey="tcHourly" />
                <PriceRow label="Day Rate (8hrs)" priceKey="tcDay" />
                <PriceRow label="Night Rate (8hrs)" priceKey="tcNight" />
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  );
}
